<template>
  <div class="dashboard-new">
    <!-- 统计卡片 -->
    <div class="stats-grid">
      <div class="stat-card" @click="$router.push('/devices')">
        <div class="stat-icon device-icon"></div>
        <div class="stat-content">
          <div class="stat-number">{{ statistics.totalDevices }}</div>
          <div class="stat-label">设备总数-</div>
        </div>
      </div>
      
      <div class="stat-card" @click="$router.push('/devices')">
        <div class="stat-icon online-icon"></div>
        <div class="stat-content">
          <div class="stat-number">{{ statistics.onlineDevices }}</div>
          <div class="stat-label">在线设备</div>
        </div>
      </div>
      
      <div class="stat-card" @click="$router.push('/alarms')">
        <div class="stat-icon alarm-icon"></div>
        <div class="stat-content">
          <div class="stat-number">{{ statistics.pendingAlarms }}</div>
          <div class="stat-label">待处理告警</div>
        </div>
      </div>
      
      <div class="stat-card" @click="$router.push('/family')">
        <div class="stat-icon family-icon"></div>
        <div class="stat-content">
          <div class="stat-number">{{ statistics.familyMembers }}</div>
          <div class="stat-label">家庭成员</div>
        </div>
      </div>
    </div>

    <!-- 图表区域 -->
    <div class="charts-grid">
      <div class="chart-card">
        <div class="chart-header">
          <span class="chart-title">设备状态分布</span>
          <button class="refresh-btn" @click="refreshCharts" :disabled="loading">
            <el-icon v-if="loading" class="is-loading"><Loading /></el-icon>
            <span v-else>刷新</span>
          </button>
        </div>
        <div class="chart-container">
          <div class="device-status-chart">
            <div 
              v-for="item in deviceStatusData" 
              :key="item.name"
              class="status-bar"
              :style="{ 
                background: item.color,
                width: `${getPercentage(item.value)}%`
              }"
            >
              <span class="bar-label">{{ item.name }}: {{ item.value }}台</span>
            </div>
          </div>
        </div>
      </div>
      
      <div class="chart-card">
        <div class="chart-header">
          <span class="chart-title">告警趋势</span>
          <div class="chart-controls">
            <select v-model="alarmTrendDays" @change="refreshCharts" class="days-select">
              <option :value="7">7天</option>
              <option :value="30">30天</option>
            </select>
            <button class="refresh-btn" @click="refreshCharts" :disabled="loading">
              <el-icon v-if="loading" class="is-loading"><Loading /></el-icon>
              <span v-else>刷新</span>
            </button>
          </div>
        </div>
        <div class="chart-container">
          <div class="alarm-trend-chart">
            <div class="chart-area">
              <div class="y-axis">
                <div v-for="i in 5" :key="i" class="y-label">{{ Math.ceil(maxAlarmValue / 4) * (5-i) }}</div>
              </div>
              <div class="plot-area">
                <svg width="100%" height="150" class="trend-svg">
                  <!-- 背景网格 -->
                  <defs>
                    <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                      <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#f0f0f0" stroke-width="1"/>
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid)" />
                  
                  <!-- 数据线 -->
                  <polyline
                    v-if="alarmTrendData.length > 0"
                    :points="getLinePoints()"
                    fill="none"
                    stroke="#f56c6c"
                    stroke-width="2"
                  />
                  
                  <!-- 数据点 -->
                  <circle
                    v-for="(value, index) in alarmTrendData"
                    :key="index"
                    :cx="`${(index / Math.max(alarmTrendData.length - 1, 1)) * 100}%`"
                    :cy="`${150 - (value / Math.max(maxAlarmValue, 1)) * 130}`"
                    r="4"
                    fill="#f56c6c"
                  />
                </svg>
              </div>
            </div>
            <div class="x-axis">
              <div 
                v-for="(label, index) in alarmTrendLabels" 
                :key="index" 
                class="x-label"
              >
                {{ label }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 最近告警 -->
    <div class="recent-alarms-card">
      <div class="card-header">
        <span class="card-title">最近告警</span>
        <div class="header-actions">
          <button class="action-btn" @click="viewAllAlarms">查看全部</button>
          <button class="refresh-btn" @click="refreshAlarms" :disabled="loading">
            <el-icon v-if="loading" class="is-loading"><Loading /></el-icon>
            <span v-else>刷新</span>
          </button>
        </div>
      </div>
      
      <div class="table-container">
        <table class="alarms-table">
          <thead>
            <tr>
              <th>告警标题</th>
              <th>设备名称</th>
              <th>告警类型</th>
              <th>级别</th>
              <th>状态</th>
              <th>发生时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="recentAlarms.length === 0">
              <td colspan="7" class="no-data">暂无告警数据</td>
            </tr>
            <tr v-for="alarm in recentAlarms" :key="alarm.id">
              <td class="alarm-title">{{ alarm.title }}</td>
              <td>{{ alarm.deviceName }}</td>
              <td>{{ getAlarmTypeText(alarm.alarmType) }}</td>
              <td>
                <span :class="`tag tag-${getAlarmLevelType(alarm.alarmLevelName)}`">
                  {{ alarm.alarmLevelName }}
                </span>
              </td>
              <td>
                <span :class="`tag tag-${getAlarmStatusType(alarm.status)}`">
                  {{ getStatusText(alarm.status) }}
                </span>
              </td>
              <td class="time-cell">{{ alarm.createdAt }}</td>
              <td class="action-cell">
                <button 
                  v-if="alarm.status === 'PENDING'" 
                  @click="confirmAlarm(alarm.id)" 
                  class="action-btn primary">
                  确认
                </button>
                <button 
                  v-if="alarm.status === 'CONFIRMED'" 
                  @click="showAdvancedResolveModal(alarm)" 
                  class="action-btn resolve">
                  处理
                </button>
                <button 
                  @click="viewAlarmDetail(alarm)" 
                  class="action-btn detail">
                  详情
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 快速操作 -->
    <div class="quick-actions">
      <div class="card-title">快速操作</div>
      <div class="action-buttons">
        <button class="quick-btn devices" @click="$router.push('/devices')">
          <span class="btn-icon">📱</span>
          <span>设备管理</span>
        </button>
        <button class="quick-btn alarms" @click="$router.push('/alarms')">
          <span class="btn-icon">🚨</span>
          <span>告警中心</span>
        </button>
        <button class="quick-btn family" @click="$router.push('/family')">
          <span class="btn-icon">👨‍👩‍👧‍👦</span>
          <span>家庭管理</span>
        </button>
        <button class="quick-btn health" @click="$router.push('/health')">
          <span class="btn-icon">📊</span>
          <span>健康分析</span>
        </button>
      </div>
    </div>

    <!-- 高级处理告警对话框 -->
    <div v-if="showAdvancedResolveDialog" class="modal-overlay" @click="closeAdvancedResolveModal">
      <div class="modal large" @click.stop>
        <div class="modal-header">
          <h3>处理告警</h3>
          <button @click="closeAdvancedResolveModal" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>告警信息:</label>
            <div class="alarm-info">
              <div><strong>{{ currentAlarm?.title }}</strong></div>
              <div>{{ currentAlarm?.content }}</div>
              <div class="alarm-meta">
                <span class="device-info">设备：{{ currentAlarm?.deviceName }}</span>
              </div>
            </div>
          </div>
          
          <div class="form-group">
            <label>处理动作: <span class="required">*</span></label>
            <select v-model="resolveActionType" class="action-select">
              <option value="">请选择处理动作</option>
              <option value="IN_PROGRESS">处理中 - 正在处理中，暂未完成</option>
              <option value="TEST_ALARM">测试告警 - 设备功能测试产生的告警</option>
              <option value="FALSE_ALARM">误报 - 设备误报或环境因素导致</option>
              <option value="REAL_ALARM">真实告警 - 真实的安全事件，已处理</option>
            </select>
          </div>
          
          <div class="form-group">
            <label>处理说明: <span class="required">*</span></label>
            <textarea 
              v-model="advancedResolveNote" 
              placeholder="请详细描述处理过程和结果..."
              rows="4"
              class="full-width"></textarea>
          </div>
          
          <div class="form-group">
            <label>现场照片: <small>(最多5张，支持JPG、PNG、GIF、WebP格式，单张不超过5MB)</small></label>
            <div class="photo-upload-area">
              <div class="upload-zone" @click="triggerFileInput" @dragover.prevent @drop.prevent="handleFileDrop">
                <input 
                  ref="fileInput" 
                  type="file" 
                  multiple 
                  accept="image/*" 
                  @change="handleFileSelect" 
                  style="display: none;">
                <div class="upload-hint">
                  <div class="upload-icon">📷</div>
                  <div>点击或拖拽上传照片</div>
                </div>
              </div>
              
              <div v-if="selectedFiles.length > 0" class="selected-files">
                <div v-for="(file, index) in selectedFiles" :key="index" class="file-item">
                  <img :src="getFilePreview(file)" alt="预览" class="file-preview">
                  <div class="file-info">
                    <div class="file-name">{{ file.name }}</div>
                    <div class="file-size">{{ formatFileSize(file.size) }}</div>
                  </div>
                  <button @click="removeFile(index)" class="remove-file">×</button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeAdvancedResolveModal" class="btn secondary">取消</button>
          <button @click="uploadPhotosAndResolve" class="btn primary" :disabled="!resolveActionType || !advancedResolveNote">
            <span v-if="uploading">处理中...</span>
            <span v-else>确认处理</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Loading } from '@element-plus/icons-vue'
import { dashboardApi } from '@/utils/api'
import api from '@/utils/api'

export default {
  name: 'DashboardNew',
  components: {
    Loading
  },
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const statistics = ref({
      totalDevices: 0,
      onlineDevices: 0,
      pendingAlarms: 0,
      familyMembers: 0
    })
    const recentAlarms = ref([])
    const alarmTrendDays = ref(7)
    
    // 图表数据
    const deviceStatusData = ref([
      { name: '在线', value: 0, color: '#67C23A' },
      { name: '离线', value: 0, color: '#909399' },
      { name: '警告', value: 0, color: '#E6A23C' },
      { name: '错误', value: 0, color: '#F56C6C' }
    ])
    
    const alarmTrendData = ref([])
    const alarmTrendLabels = ref([])

    // 计算属性
    const totalDevices = computed(() => {
      return deviceStatusData.value.reduce((sum, item) => sum + item.value, 0)
    })

    const maxAlarmValue = computed(() => {
      return Math.max(...alarmTrendData.value, 1)
    })

    // 方法
    const getPercentage = (value) => {
      const total = totalDevices.value
      return total > 0 ? (value / total) * 100 : 0
    }

    const getLinePoints = () => {
      if (alarmTrendData.value.length === 0) return ''
      
      return alarmTrendData.value.map((value, index) => {
        const x = (index / Math.max(alarmTrendData.value.length - 1, 1)) * 100
        const y = 150 - (value / Math.max(maxAlarmValue.value, 1)) * 130
        return `${x},${y}`
      }).join(' ')
    }

    const loadSampleData = () => {
      statistics.value = {
        totalDevices: 12,
        onlineDevices: 10,
        pendingAlarms: 3,
        familyMembers: 5
      }
      
      deviceStatusData.value = [
        { name: '在线', value: 10, color: '#67C23A' },
        { name: '离线', value: 2, color: '#909399' },
        { name: '警告', value: 0, color: '#E6A23C' },
        { name: '错误', value: 0, color: '#F56C6C' }
      ]
      
      const today = new Date()
      alarmTrendLabels.value = Array.from({length: 7}, (_, i) => {
        const date = new Date(today)
        date.setDate(date.getDate() - (6 - i))
        return `${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
      })
      alarmTrendData.value = [2, 1, 3, 0, 1, 2, 3]
      
      recentAlarms.value = [
        {
          id: 1,
          title: '烟雾告警',
          deviceName: '客厅烟感器',
          alarmType: 'SMOKE_ALARM',
          level: '紧急',
          status: 'PENDING',
          createdAt: '2024-01-15 14:30:25'
        },
        {
          id: 2,
          title: '电量告警',
          deviceName: '卧室门磁',
          alarmType: 'LOW_BATTERY',
          level: '低',
          status: 'CONFIRMED',
          createdAt: '2024-01-15 13:45:10'
        },
        {
          id: 3,
          title: '设备离线',
          deviceName: '厨房燃气传感器',
          alarmType: 'DEVICE_OFFLINE',
          level: '中',
          status: 'RESOLVED',
          createdAt: '2024-01-15 12:20:35'
        }
      ]
    }

    const loadDashboardData = async () => {
      loading.value = true
      try {
        // 尝试加载真实数据
        const [statsResponse, deviceChartResponse, alarmChartResponse, alarmsResponse] = await Promise.allSettled([
          dashboardApi.getStatistics(),
          dashboardApi.getDeviceStatusChart(),
          dashboardApi.getAlarmTrendChart(alarmTrendDays.value),
          dashboardApi.getRecentAlarms(10)
        ])

        // 处理统计数据
        if (statsResponse.status === 'fulfilled') {
          statistics.value = statsResponse.value.data
        }

        // 处理设备状态图表
        if (deviceChartResponse.status === 'fulfilled') {
          deviceStatusData.value = deviceChartResponse.value.data.data
        }

        // 处理告警趋势图表
        if (alarmChartResponse.status === 'fulfilled') {
          const chartData = alarmChartResponse.value.data
          alarmTrendLabels.value = chartData.labels
          alarmTrendData.value = chartData.data
        }

        // 处理最近告警
        if (alarmsResponse.status === 'fulfilled') {
          recentAlarms.value = alarmsResponse.value.data.alarms || []
          console.log('最近告警数据:', recentAlarms.value)
        }

      } catch (error) {
        console.error('加载控制台数据失败:', error)
      } finally {
        loading.value = false
      }
    }

    const refreshCharts = async () => {
      await loadDashboardData()
      ElMessage.success('数据已刷新')
    }

    const refreshAlarms = async () => {
      loading.value = true
      try {
        const response = await dashboardApi.getRecentAlarms(10)
        recentAlarms.value = response.data.alarms || []
        ElMessage.success('告警数据已刷新')
      } catch (error) {
        console.error('获取告警数据失败:', error)
      } finally {
        loading.value = false
      }
    }

    const viewAllAlarms = () => {
      router.push('/alarms')
    }

    const handleAlarm = (alarm) => {
      if (alarm.status === 'PENDING') {
        confirmAlarm(alarm.id)
      } else if (alarm.status === 'CONFIRMED') {
        showAdvancedResolveModal(alarm)
      } else {
        router.push(`/alarms/${alarm.id}`)
      }
    }

    const viewAlarmDetail = (alarm) => {
      router.push(`/alarms/${alarm.id}`)
    }

    const getAlarmTypeText = (type) => {
      const typeMap = {
        'SMOKE_ALARM': '烟雾检测',
        'LOW_BATTERY': '低电量',
        'DEVICE_OFFLINE': '设备离线',
        'INTRUSION': '入侵检测',
        'TEMPERATURE': '温度异常'
      }
      return typeMap[type] || type
    }

    const getAlarmLevelType = (level) => {
      const types = {
        '紧急': 'danger',
        '高': 'warning',
        '中': 'info',
        '低': 'success'
      }
      return types[level] || 'info'
    }

    const getAlarmStatusType = (status) => {
      const types = {
        'PENDING': 'warning',
        'CONFIRMED': 'info',
        'RESOLVED': 'success',
        'IGNORED': 'info'
      }
      return types[status] || 'info'
    }

    const getStatusText = (status) => {
      const texts = {
        'PENDING': '待处理',
        'CONFIRMED': '已确认',
        'IN_PROGRESS': '处理中',
        'RESOLVED': '已解决',
        'COMPLETED': '已处理',
        'CLOSED': '已关闭',
        'IGNORED': '已忽略'
      }
      return texts[status] || status
    }

    // 告警处理相关功能
    const confirmAlarm = async (alarmId) => {
      try {
        loading.value = true
        const response = await api.post(`/alarms/${alarmId}/confirm`)
        if (response && response.success) {
          ElMessage.success('告警确认成功')
          await refreshAlarms()
        } else {
          ElMessage.error(response?.message || '确认失败')
        }
      } catch (error) {
        console.error('确认告警失败:', error)
        ElMessage.error('确认告警失败')
      } finally {
        loading.value = false
      }
    }

    // 高级处理对话框
    const showAdvancedResolveDialog = ref(false)
    const currentAlarm = ref(null)
    const advancedResolveNote = ref('')
    const resolveActionType = ref('')
    const selectedFiles = ref([])
    const uploading = ref(false)

    const showAdvancedResolveModal = (alarm) => {
      currentAlarm.value = alarm
      advancedResolveNote.value = ''
      resolveActionType.value = ''
      selectedFiles.value = []
      showAdvancedResolveDialog.value = true
    }

    const closeAdvancedResolveModal = () => {
      showAdvancedResolveDialog.value = false
      currentAlarm.value = null
      advancedResolveNote.value = ''
      resolveActionType.value = ''
      selectedFiles.value = []
      uploading.value = false
    }

    const triggerFileInput = () => {
      const fileInput = document.querySelector('input[type="file"]')
      if (fileInput) {
        fileInput.click()
      }
    }

    const handleFileSelect = (event) => {
      const files = Array.from(event.target.files)
      addFiles(files)
    }

    const handleFileDrop = (event) => {
      const files = Array.from(event.dataTransfer.files)
      addFiles(files)
    }

    const addFiles = (files) => {
      if (selectedFiles.value.length + files.length > 5) {
        ElMessage.warning('最多只能上传5张照片')
        return
      }
      
      const validFiles = files.filter(file => {
        if (!file.type.startsWith('image/')) {
          ElMessage.error(`文件 ${file.name} 不是图片格式`)
          return false
        }
        
        if (file.size > 5 * 1024 * 1024) {
          ElMessage.error(`文件 ${file.name} 大小超过5MB`)
          return false
        }
        
        return true
      })
      
      selectedFiles.value.push(...validFiles)
    }

    const removeFile = (index) => {
      selectedFiles.value.splice(index, 1)
    }

    const getFilePreview = (file) => {
      return URL.createObjectURL(file)
    }

    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    const uploadPhotosAndResolve = async () => {
      if (!resolveActionType.value || !advancedResolveNote.value.trim()) {
        ElMessage.warning('请选择处理动作并填写处理说明')
        return
      }
      
      uploading.value = true
      let photoUrls = []
      
      try {
        // 上传照片
        if (selectedFiles.value.length > 0) {
          const formData = new FormData()
          selectedFiles.value.forEach(file => {
            formData.append('files', file)
          })
          formData.append('alarmId', currentAlarm.value.id)
          
          const uploadResponse = await api.post('/files/upload/alarm-photos', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })
          
          if (uploadResponse && uploadResponse.success) {
            photoUrls = uploadResponse.data.urls
          } else {
            throw new Error(uploadResponse?.message || '照片上传失败')
          }
        }
        
        // 提交告警处理
        const response = await api.post(`/alarms/${currentAlarm.value.id}/resolve-advanced`, {
          resolveNote: advancedResolveNote.value,
          resolveAction: resolveActionType.value,
          resolvePhotos: JSON.stringify(photoUrls)
        })
        
        if (response && response.success) {
          ElMessage.success('告警处理成功')
          closeAdvancedResolveModal()
          await refreshAlarms()
        } else {
          ElMessage.error(response?.message || '处理失败')
        }
      } catch (error) {
        console.error('告警处理失败:', error)
        ElMessage.error('告警处理失败: ' + (error.message || '未知错误'))
      } finally {
        uploading.value = false
      }
    }

    onMounted(() => {
      loadDashboardData()
    })

    return {
      loading,
      statistics,
      recentAlarms,
      alarmTrendDays,
      deviceStatusData,
      alarmTrendData,
      alarmTrendLabels,
      totalDevices,
      maxAlarmValue,
      getPercentage,
      getLinePoints,
      refreshCharts,
      refreshAlarms,
      viewAllAlarms,
      handleAlarm,
      viewAlarmDetail,
      getAlarmTypeText,
      getAlarmLevelType,
      getAlarmStatusType,
      getStatusText,
      confirmAlarm,
      showAdvancedResolveDialog,
      currentAlarm,
      advancedResolveNote,
      resolveActionType,
      selectedFiles,
      uploading,
      showAdvancedResolveModal,
      closeAdvancedResolveModal,
      triggerFileInput,
      handleFileSelect,
      handleFileDrop,
      removeFile,
      getFilePreview,
      formatFileSize,
      uploadPhotosAndResolve
    }
  }
}
</script>

<style scoped>
.dashboard-new {
  padding: 0 16px;
  max-width: 100%;
  margin: 0;
}

/* 统计卡片样式 */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  flex-shrink: 0;
}

.device-icon { background: linear-gradient(135deg, #409EFF, #67C23A); }
.online-icon { background: linear-gradient(135deg, #67C23A, #E6A23C); }
.alarm-icon { background: linear-gradient(135deg, #F56C6C, #E6A23C); }
.family-icon { background: linear-gradient(135deg, #909399, #409EFF); }

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 28px;
  font-weight: bold;
  color: #409EFF;
  margin-bottom: 4px;
  line-height: 1;
}

.stat-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

/* 图表区域样式 */
.charts-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

.chart-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.chart-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.chart-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.days-select {
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 12px;
}

.refresh-btn {
  padding: 6px 12px;
  background: #409EFF;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.chart-container {
  height: 240px;
}

/* 设备状态图表 */
.device-status-chart {
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  justify-content: center;
}

.status-bar {
  height: 32px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 500;
  font-size: 14px;
  min-width: 80px;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
}

/* 告警趋势图表 */
.alarm-trend-chart {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.chart-area {
  flex: 1;
  display: flex;
  gap: 8px;
}

.y-axis {
  width: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 8px 0;
}

.y-label {
  font-size: 10px;
  color: #666;
  text-align: right;
}

.plot-area {
  flex: 1;
}

.trend-svg {
  width: 100%;
  height: 150px;
}

.x-axis {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  padding-left: 32px;
}

.x-label {
  font-size: 10px;
  color: #666;
  text-align: center;
}

/* 最近告警样式 */
.recent-alarms-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 12px;
}

.action-btn.primary {
  background: #409EFF;
  color: white;
  border-color: #409EFF;
}

.action-btn.resolve {
  background: #67C23A;
  color: white;
  border-color: #67C23A;
}

.table-container {
  overflow-x: auto;
}

.alarms-table {
  width: 100%;
  border-collapse: collapse;
}

.alarms-table th,
.alarms-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.alarms-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #666;
  font-size: 14px;
}

.no-data {
  text-align: center;
  color: #999;
  font-style: italic;
}

.alarm-title {
  font-weight: 500;
}

.tag {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.tag-danger { background: #fef0f0; color: #f56c6c; }
.tag-warning { background: #fdf6ec; color: #e6a23c; }
.tag-success { background: #f0f9ff; color: #67c23a; }
.tag-info { background: #f4f4f5; color: #909399; }

.time-cell {
  font-size: 12px;
  color: #666;
}

.action-cell {
  white-space: nowrap;
}

/* 快速操作样式 */
.quick-actions {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.action-buttons {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.quick-btn {
  padding: 16px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.quick-btn.devices { background: linear-gradient(135deg, #409EFF, #67C23A); }
.quick-btn.alarms { background: linear-gradient(135deg, #F56C6C, #E6A23C); }
.quick-btn.family { background: linear-gradient(135deg, #67C23A, #409EFF); }
.quick-btn.health { background: linear-gradient(135deg, #909399, #67C23A); }

.quick-btn {
  color: white;
}

.quick-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}

.btn-icon {
  font-size: 24px;
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .dashboard-new {
    padding: 0 12px;
  }
  
  .stats-grid {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 12px;
  }
}

@media (max-width: 768px) {
  .dashboard-new {
    padding: 0 8px;
  }
  
  .charts-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  
  .action-buttons {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .action-buttons {
    grid-template-columns: 1fr;
  }
}

/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal.large {
  max-width: 700px;
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
}

.form-group textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  resize: vertical;
  box-sizing: border-box;
}

.alarm-info {
  background: #fafafa;
  padding: 12px;
  border-radius: 4px;
}

.alarm-meta {
  margin-top: 8px;
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #666;
}

.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn.primary {
  background: #409EFF;
  color: white;
}

.btn.primary:disabled {
  background: #d9d9d9;
  color: #999;
  cursor: not-allowed;
}

.btn.secondary {
  background: #f0f0f0;
  color: #666;
}

.required {
  color: #f5222d;
}

.action-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  font-size: 14px;
  box-sizing: border-box;
}

.full-width {
  width: 100%;
}

.photo-upload-area {
  border: 2px dashed #d9d9d9;
  border-radius: 6px;
  padding: 16px;
}

.upload-zone {
  text-align: center;
  padding: 20px;
  cursor: pointer;
}

.upload-zone:hover {
  background: #fafafa;
}

.upload-hint {
  color: #666;
}

.upload-icon {
  font-size: 24px;
  margin-bottom: 8px;
}

.selected-files {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
}

.file-item {
  position: relative;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  overflow: hidden;
  background: white;
}

.file-preview {
  width: 100%;
  height: 80px;
  object-fit: cover;
}

.file-info {
  padding: 8px;
}

.file-name {
  font-size: 12px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-size {
  font-size: 11px;
  color: #666;
  margin-top: 2px;
}

.remove-file {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>